import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { ClipboardList, Plus, Pencil, Globe } from "lucide-react";
import { useState } from "react";
import professionalProfileApi from "../../../api/professionalProfile";
import EmptyStateCard from "../../../components/professional/EmptyStateCard";
import Modal from "../../../components/professional/Modal";
import RegulatoryBodyForm from "./RegulatoryBodyForm";
import CustomLoader from "../../../components/common/CustomLoader";

// Human-readable labels for regions
const regionLabels = {
  africa: "Africa",
  europe: "Europe",
  middle_east: "Middle East",
  asia: "Asia",
  north_america: "North America",
  south_america: "South America",
  australia_oceania: "Australia / Oceania",
  global: "Global / Regional Agencies (EASA, ICAO)",
};

const RegulatoryBodyTab = ({
  regulatoryBodies,
  onModification,
  setNotification,
  dropDownOptions
}) => {
  const [open, setOpen] = useState(false);
  const [selectedBody, setSelectedBody] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleOpen = (regBody = null) => {
    setSelectedBody(regBody);
    setOpen(true);
  };

  const handleClose = () => {
    setSelectedBody(null);
    setOpen(false);
  };

  const handleSave = async (data) => {
    setLoading(true);
    try {
      let payload = { ...data };

      if (selectedBody && payload.id) {
        // Update existing
        const res = await professionalProfileApi.updateSingleResource(
          data.id,
          "regulatory-body",
          payload
        );
        if (res.status === 200) {
          onModification();
          setNotification("Regulatory body updated successfully", "success");
        }
      } else {
        // Add new
        delete payload.id;
        const res = await professionalProfileApi.updateProfessionalProfile({
          regulatory_bodies: [payload],
        });
        if (res.status === 200 || res.status === 201) {
          onModification();
          setNotification("Regulatory body added successfully", "success");
        }
      }
      handleClose();
    } catch (error) {
      console.error(error);
      setNotification(
        "An error occurred while updating regulatory body",
        "error"
      );
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <CustomLoader />;

  const dialogTitle = selectedBody
    ? "Edit Regulatory Body"
    : "Add Regulatory Body";

  return (
    <div>
      {/* Empty State */}
      {regulatoryBodies?.length === 0 ? (
        <EmptyStateCard
          icon={<Globe size={48} color="#1976d2" />}
          title="No regulatory body added yet"
          description="Add your Civil Aviation Authority or regional regulatory body."
          buttonText="Add Regulatory Body"
          handleOpen={handleOpen}
        />
      ) : (
        <>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
              mb: 3,
            }}
          >
            <Typography variant="h6">Regulatory Bodies</Typography>
            <Button
              startIcon={<Plus size={18} />}
              variant="contained"
              color="primary"
              onClick={() => handleOpen()}
            >
              Add Regulatory Body
            </Button>
          </Box>

          {/* Body Cards */}
          <Grid container spacing={3}>
            {regulatoryBodies.map((body) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={body.id}>
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                    transition: "0.2s",
                    "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.15)" },
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <CardContent>
                    {/* Authority Name */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <ClipboardList size={16} style={{ marginRight: 8 }} />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                        color="text.primary"
                      >
                        {body.authority_name}
                      </Typography>
                    </Box>

                    {/* Country */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Country: {body.country}
                    </Typography>

                    {/* Abbreviation */}
                    {body.abbreviation && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Abbreviation: {body.abbreviation}
                      </Typography>
                    )}

                    {/* Region */}
                    <Typography variant="body2" color="text.secondary">
                      Region: {regionLabels[body.region] || body.region}
                    </Typography>
                  </CardContent>

                  {/* Actions */}
                  <CardActions sx={{ justifyContent: "flex-end" }}>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpen(body)}
                    >
                      <Pencil size={18} />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Add/Edit Dialog */}
      <Modal handleClose={handleClose} open={open} title={dialogTitle}>
        <RegulatoryBodyForm
          onSubmit={handleSave}
          onCancel={handleClose}
          regulatoryBody={selectedBody}
          dropDownOptions={dropDownOptions}
        />
      </Modal>
    </div>
  );
};

export default RegulatoryBodyTab;
